import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Option, Select, Spinner, UserSelectorMode, UsersSelector } from '@bringg/react-components';
import _debounce from 'lodash/debounce';
import Bringg, { SetOptional } from '@bringg/types';
import { useTranslation } from 'react-i18next';
import { LookupConsts } from '@bringg/dashboard-sdk';
import { UsersSearchResponse, UsersSearchResponseItem } from '@bringg/dashboard-sdk/dist/LookUp/lookup.consts';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { getSharedRootStore } from '@bringg-frontend/global-stores';
import { TypographyColor, TypographyHelper } from '@bringg-frontend/ui';

import styles from './async-user-selector.module.scss';

type UserSelectorProps = React.ComponentProps<typeof UsersSelector>;

type BaseSearch = Omit<LookupConsts.UsersSearchQuery, 'query'>;

interface Props
	extends SetOptional<
		Pick<UserSelectorProps, 'isDisabled' | 'isOptionDisabled' | 'placeholder' | 'mode'>,
		'placeholder'
	> {
	onChange: (userIds: number[]) => void;

	defaultUsersIds: number[];

	baseSearch: BaseSearch;

	mode?: UserSelectorMode;

	'data-test-id'?: string;

	'aria-label'?: string;

	/** Shows each user's id next to the name, for pickers where two users can share a name. */
	showUserId?: boolean;
}

export const MINIMUM_CHARACTERS_REQUIRED_BY_SEARCH_API = 2;

export const AsyncUsersSelector = ({
	onChange,
	isDisabled,
	placeholder,
	baseSearch,
	defaultUsersIds,
	mode = UserSelectorMode.multiple,
	'data-test-id': dataTestId,
	'aria-label': ariaLabel,
	showUserId = false
}: Props) => {
	const { t } = useTranslation();
	const placeholderText = placeholder || t('USERS_SELECTOR_MODAL.PLACEHOLDER') || 'select user';

	const [defaultSelectedUsersFetched, setDefaultSelectedUsersFetched] = useState(false);
	const [isFetching, setIsFetching] = useState(false);
	const ac = useRef(new AbortController());
	const [users, setUsers] = useState<(Bringg.User | UsersSearchResponseItem)[]>([]);

	const searchDebounce = useCallback(
		_debounce(async (value: string, searchMixin: BaseSearch) => {
			ac.current.abort();
			ac.current = new AbortController();

			const currentSignal = ac.current.signal;

			let foundedUsers: UsersSearchResponseItem[] = [];
			try {
				foundedUsers = await searchUsers({
					...searchMixin,
					query: value,
					signal: currentSignal
				});

				setUsers(foundedUsers || []);
			} catch (error) {
				console.error('Failed to search users', error);
			} finally {
				setIsFetching(false);
			}

			if (currentSignal.aborted || !foundedUsers?.length) return;

			try {
				const userIds = foundedUsers.map(user => user.id);

				const { driversStore } = getSharedRootStore().data;
				const usersToSet = await driversStore.loadMany(userIds, {
					useCache: true,
					signal: currentSignal
				});

				if (currentSignal.aborted) return;

				setUsers(usersToSet as Bringg.User[]);
			} catch (e) {
				console.error('Failed to fetch users', e);
			}
		}, 500),
		[]
	);

	useEffect(() => {
		async function fetchDefaultSelected(ids: number[]) {
			try {
				const { driversStore } = getSharedRootStore().data;
				const usersToSet = await driversStore.loadMany(ids);

				setUsers(usersToSet as Bringg.User[]);
			} catch (error) {
				console.error('Failed to fetch default users', error);
			} finally {
				setDefaultSelectedUsersFetched(true);
			}
		}

		setDefaultSelectedUsersFetched(false);

		void fetchDefaultSelected(defaultUsersIds);
	}, [defaultUsersIds]);

	useEffect(() => {
		return () => {
			ac.current.abort();
		};
	}, []);

	if (defaultUsersIds?.length && !defaultSelectedUsersFetched) {
		return null;
	}

	const notFoundContent = isFetching ? <Spinner size="small" /> : null;

	const onSearch = (value: string) => {
		if (value.length < MINIMUM_CHARACTERS_REQUIRED_BY_SEARCH_API) {
			return;
		}

		setIsFetching(true);

		// Can return undefined when the user never typed anything
		searchDebounce(value, baseSearch)?.catch(error => console.error('Failed to search users', error));
	};

	if (showUserId) {
		// UsersSelector builds its option labels itself, so the id column needs its own Select.
		return (
			<div className="users-selector">
				<Select
					data-test-id={dataTestId}
					aria-label={ariaLabel}
					mode={mode === UserSelectorMode.single ? undefined : UserSelectorMode.multiple}
					showSearch
					allowClear
					className="users-select"
					popupClassName="users-select-dropdown"
					dropdownMatchSelectWidth={false}
					defaultValue={users.map(user => user.id)}
					placeholder={placeholderText}
					disabled={isDisabled}
					notFoundContent={notFoundContent}
					filterOption={(input: string, option) =>
						String(option?.searchText ?? '')
							.toLowerCase()
							.includes(input.toLowerCase())
					}
					onChange={onChange}
					onSearch={onSearch}
				>
					{users.map(user => (
						<Option key={user.id} value={user.id} searchText={`${user.name} ${user.id}`}>
							<span className={styles.option}>
								<span className={styles.name}>{user.name}</span>
								<TypographyHelper tag="span" color={TypographyColor.Subtle} className={styles.id}>
									{user.id}
								</TypographyHelper>
							</span>
						</Option>
					))}
				</Select>
			</div>
		);
	}

	return (
		<UsersSelector
			data-test-id={dataTestId}
			aria-label={ariaLabel}
			users={users}
			mode={mode}
			selectedIds={users.map(user => user.id)}
			placeholder={placeholderText}
			isDisabled={isDisabled}
			notFoundContent={notFoundContent}
			onChange={onChange}
			onSearch={onSearch}
		/>
	);
};

async function searchUsers({
	includeAllAdmins,
	roles,
	signal,
	...rest
}: LookupConsts.UsersSearchQuery & {
	signal: AbortSignal;
}): Promise<UsersSearchResponse['items']> {
	const { dashboardSdk } = getRootEnv();

	const result = await dashboardSdk.sdk.v2.lookup.getUsers(
		{
			roles,
			...rest
		},
		{ signal }
	);

	return result.items;
}
